<template>
  <div class="supplier-requests">
    <!-- Вкладки быстрых фильтров -->
    <div class="quick-filters">
      <q-btn-toggle
        v-model="quickFilter"
        no-caps
        toggle-color="primary"
        color="white"
        text-color="primary"
        :options="quickFilterOptions"
        class="quick-filter-toggle"
      />
      <UploadExcelBtn
        class="supplier-export-btn"
        :loading="isExporting"
        :disabled="isExporting"
        @click="exportMaterials"
      />
    </div>

    <!-- Фильтры -->
    <div class="filters-section" :class="{ 'filters-collapsed': !filtersVisible }">
      <div class="filters-header" @click="toggleFilters">
        <span class="filters-title">
          <q-icon :name="filtersVisible ? 'expand_less' : 'expand_more'" size="20px" />
          Фильтры
        </span>
        <span v-if="!filtersVisible" class="filters-summary">
          {{ getFiltersSummary }}
        </span>
      </div>
      <div v-show="filtersVisible" class="filters-content">
      <div class="filters-row">
        <div class="tw-p-2 tw-pt-1 tw-border tw-border-[#ccc] tw-rounded-md">
          <span class="tw-text-[12px] tw-text-[#626262]">Дата отгрузки:</span>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <div class="filter-item filter-item--date">
              <label>Дата с:</label>
              <CustomDate 
                :value="filters.date_shipping_begin" 
                placeholder="Дата" 
                :width="120"
                @change_date="(val) => filters.date_shipping_begin = val"
              />
            </div>
            <div class="filter-item filter-item--date">
              <label>Дата по:</label>
              <CustomDate 
                :value="filters.date_shipping_end" 
                placeholder="Дата" 
                :width="120"
                @change_date="(val) => filters.date_shipping_end = val"
              />
            </div>
          </div>
        </div>
        <div class="tw-p-2 tw-pt-1 tw-border tw-border-[#ccc] tw-rounded-md">
          <span class="tw-text-[12px] tw-text-[#626262]">Дата создания заявки:</span>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <div class="filter-item filter-item--date">
              <label>Дата с:</label>
              <CustomDate 
                :value="filters.date_create_begin" 
                placeholder="Дата" 
                :width="120"
                @change_date="(val) => filters.date_create_begin = val"
              />
            </div>
            <div class="filter-item filter-item--date">
              <label>Дата по:</label>
              <CustomDate 
                :value="filters.date_create_end" 
                placeholder="Дата" 
                :width="120"
                @change_date="(val) => filters.date_create_end = val"
              />
            </div>
          </div>
        </div>
        <div class="filter-item filter-item--status">
          <label>Статус:</label>
          <q-select
            v-model="filters.provider_request_status_id_arr"
            :options="availableStatuses"
            option-value="provider_request_status_id"
            option-label="provider_request_status_name"
            emit-value
            map-options
            multiple
            outlined
            dense
            placeholder="Все"
            class="status-select"
          >
            <template v-slot:selected>
              <span v-if="filters.provider_request_status_id_arr?.length === 1" class="status-single">
                {{ getStatusName(filters.provider_request_status_id_arr[0]) }}
              </span>
              <span v-else-if="filters.provider_request_status_id_arr?.length > 1" class="status-count">
                Выбрано: {{ filters.provider_request_status_id_arr.length }}
              </span>
              <span v-else class="status-placeholder">Все</span>
            </template>
            <template v-slot:option="{ itemProps, opt, selected, toggleOption }">
              <q-item v-bind="itemProps" @click="toggleOption(opt)" dense>
                <q-item-section side>
                  <q-checkbox :model-value="selected" @update:model-value="toggleOption(opt)" dense />
                </q-item-section>
                <q-item-section>
                  <q-badge :color="opt.color" :label="opt.provider_request_status_name" />
                </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
        <div class="filter-item filter-item--grow">
          <label>ЖК и Тип проекта:</label>
          <div class="filter-with-clear">
            <div class="filter-input-wrapper filter-input-wrapper--grow" @click="openProjectModal">
              <q-input
                :model-value="selectedProjectLabel"
                outlined
                dense
                readonly
                placeholder="Выберите ЖК"
                class="project-input project-input--grow"
              >
                <template v-slot:prepend>
                  <q-icon name="apartment" color="primary" />
                </template>
              </q-input>
            </div>
            <q-btn 
              v-if="filters.resident_id" 
              flat 
              round 
              dense 
              icon="close" 
              size="sm"
              color="grey-7"
              class="clear-btn"
              @click="clearProject"
            />
          </div>
        </div>
        <div class="filter-item filter-item--grow">
          <label>SBS-проект:</label>
          <div class="filter-with-clear">
            <div class="filter-input-wrapper filter-input-wrapper--grow" @click="openSbsProjectModal">
              <q-input
                :model-value="selectedSbsProjectLabel"
                outlined
                dense
                readonly
                placeholder="Выберите SBS-проект"
                class="project-input project-input--grow"
              >
                <template v-slot:prepend>
                  <q-icon name="business" color="primary" />
                </template>
              </q-input>
            </div>
            <q-btn
              v-if="filters.sbs_project_id"
              flat
              round
              dense
              icon="close"
              size="sm"
              color="grey-7"
              class="clear-btn"
              @click="clearSbsProject"
            />
          </div>
        </div>
        <div class="filter-item">
          <label>Тип потребности:</label>
          <CustomSelect
            :value="filters.required_type_id"
            :options="requiredTypes"
            option-value-key="required_type_id"
            option-label-key="required_type_name"
            placeholder="Все типы"
            clearable
            @change="(val) => filters.required_type_id = val"
          />
        </div>
      </div>
      <div class="filters-row">
        <div class="filter-item">
          <label>Инициатор:</label>
          <CustomSelect 
            :value="filters.provider_request_initiator_type_id" 
            :options="initiatorTypes" 
            option-value-key="provider_request_initiator_type_id"
            option-label-key="provider_request_initiator_type_name"
            placeholder="Все инициаторы"
            clearable
            @change="(val) => filters.provider_request_initiator_type_id = val"
          />
        </div>
        <div class="filter-item filter-item--invoice-filter" v-if="isShowAll">
          <label>Накладная в 1С:</label>
          <CustomSelect
            :value="filters.send_to_1c_filter"
            :options="sendTo1cFilterOptions"
            option-value-key="value"
            option-label-key="label"
            placeholder="Все"
            clearable
            @change="(val) => filters.send_to_1c_filter = val"
          />
        </div>
        <div class="filter-item filter-item--invoice-filter" v-if="isShowAll">
          <label>Поставщик:</label>
          <CustomSelect
            :value="filters.provider_id"
            :options="providerFilterOptions"
            option-value-key="provider_id"
            option-label-key="provider_name"
            placeholder="Все"
            clearable
            @change="(val) => filters.provider_id = val"
          />
        </div>
        <div class="filter-item filter-item--grow">
          <label>Материал:</label>
          <div class="filter-with-clear">
            <div class="filter-input-wrapper filter-input-wrapper--grow" @click="openMaterialModal">
              <q-input
                :model-value="selectedMaterialLabel"
                outlined
                dense
                readonly
                placeholder="Выберите материал"
                class="material-input material-input--grow"
              >
                <template v-slot:prepend>
                  <q-icon name="inventory_2" color="primary" />
                </template>
              </q-input>
            </div>
            <q-btn 
              v-if="filters.material_id" 
              flat 
              round 
              dense 
              icon="close" 
              size="sm"
              color="grey-7"
              class="clear-btn"
              @click="clearMaterial"
            />
          </div>
        </div>
      </div>
      <div class="filters-row">
        <div class="filter-item filter-item--small">
          <label>ID заявки:</label>
          <q-input
            v-model.number="filters.provider_request_id"
            outlined
            dense
            type="number"
            placeholder="ID"
            clearable
          />
        </div>
        <div class="filter-item filter-item--small">
          <label>№ накладной:</label>
          <q-input
            v-model="filters.invoice_num"
            outlined
            dense
            placeholder="Номер"
            clearable
          />
        </div>
        <div class="filter-item filter-item--medium">
          <label>ID накладной:</label>
          <q-input
            v-model.number="filters.provider_request_invoice_id"
            outlined
            dense
            type="number"
            placeholder="ID"
            clearable
          />
        </div>
        <div class="filter-item filter-item--invoice-filter">
          <label>Накладные:</label>
          <CustomSelect
            :value="filters.invoice_filter_type"
            :options="invoiceFilterOptions"
            option-value-key="value"
            option-label-key="label"
            placeholder="Все"
            clearable
            @change="(val) => filters.invoice_filter_type = val"
          />
        </div>
        <div class="filter-actions">
          <q-btn color="primary" label="Применить" @click="applyFilters" :loading="isFetching" />
          <q-btn color="grey-7" flat label="Сбросить" @click="resetFilters" />
        </div>
      </div>
      </div>
    </div>

    <!-- Таблица заявок -->
    <div class="table-section">
      <CustomTable 
        title="Заявки поставщикам" 
        :rows-data="groupedRowsData" 
        :row-key="getRequestTableRowKey"
        :columns="this.requestsColumnsList" 
        :loading="isFetching"
        :custom-body="true" 
        :show-search="true"
      >
        <template #top-right>
          <div class="table-sort-toolbar tw-flex tw-items-center tw-gap-2">
            <span class="table-sort-label tw-text-[12px] tw-text-[#626262] tw-whitespace-nowrap">Сортировка</span>
            <CustomSelect
              class="table-sort-select"
              :value="listSortMode"
              :options="listSortOptions"
              option-value-key="value"
              option-label-key="label"
              :use-input="false"
              placeholder="Вид списка"
              @change="(val) => { listSortMode = val || 'shipping_grouped'; }"
            />
          </div>
        </template>
        <template #customBody="props">
          <!-- Строка-заголовок группы (дата) -->
          <q-tr v-if="props.row._isGroupHeader" class="group-header-row">
            <td :colspan="this.requestsColumnsList.length" class="group-header-cell">
              <div class="group-header-content">
                <span class="group-date-label">{{ props.row._groupLabel }}</span>
              </div>
            </td>
          </q-tr>
          
          <!-- Обычная строка заявки -->
          <q-tr 
            v-else
            :class="{ 
              'expanded-row': expandedRows[props.row.provider_request_id],
              'selected-request-row': isRequestSelected(props.row)
            }"
            @click="toggleExpand(props.row)"
            style="cursor: pointer;"
          >
            <!-- Чекбокс для мульти-выбора -->
            <custom-table-cell v-if="showActions" align="center" @click.stop>
              <q-checkbox
                :model-value="isRequestSelected(props.row)"
                @update:model-value="toggleRequestSelection(props.row)"
                size="sm"
                :disable="!props.row.can_create_invoice"
                :title="props.row.can_create_invoice ? 'Выбрать для общей накладной' : 'Нет позиций для накладной'"
              />
            </custom-table-cell>
            <!-- ID / Инициатор -->
            <custom-table-cell align="center">
              <div class="id-initiator" :title="'Заявка #' + props.row.provider_request_id">
                <strong>{{ props.row.provider_request_id }}</strong>
                <span 
                  v-if="props.row.provider_request_initiator_type_id"
                  :title="'Инициатор: ' + (props.row.provider_request_initiator_type_name || 'Не указан')"
                >
                  <q-badge 
                    :color="props.row.provider_request_initiator_type_id === 1 ? 'teal' : 'purple'" 
                    dense
                    :label="getInitiatorShort(props.row.provider_request_initiator_type_id)"
                  />
                </span>
              </div>
            </custom-table-cell>
            <!-- Дата создания -->
            <custom-table-cell align="center">
              <span
                class="date-created-cell"
                :title="props.row.date_send ? requestCreatedTitle(props.row.date_send) : ''"
              >
                {{ props.row.date_send ? formatDate(props.row.date_send) : '—' }}
              </span>
            </custom-table-cell>
            <!-- ЖК -->
            <custom-table-cell align="left">
              <div class="project-info" :title="'ЖК: ' + props.row.resident_name + '\nТип проекта: ' + props.row.project_type_name">
                <strong>{{ props.row.resident_name }}</strong>
                <span class="project-type">{{ props.row.project_type_name }}</span>
              </div>
            </custom-table-cell>
            <!-- Поставщик / Тип закупки -->
            <custom-table-cell align="left">
              <div class="provider-info" :title="'Поставщик: ' + props.row.provider_name + '\nТип закупки: ' + props.row.provider_request_purchase_type_name">
                <span class="provider-name">{{ props.row.provider_name }}</span>
                <q-badge 
                  class="purchase-type-badge"
                  multi-line
                  :color="props.row.provider_request_purchase_type_id === 1 ? 'blue' : 'orange'" 
                  dense
                  :label="props.row.provider_request_purchase_type_name || '-'"
                />
              </div>
            </custom-table-cell>
            <!-- Статус -->
            <custom-table-cell align="center">
              <div class="status-badges-list" :title="'Статусы позиций:\n' + props.row.provider_request_status_name">
                <q-badge 
                  v-for="(status, idx) in parseStatusSummary(props.row.provider_request_status_name)" 
                  :key="idx"
                  :color="status.color"
                  class="status-badge-item"
                  multi-line
                >
                  {{ status.name }} ({{ status.count }})
                </q-badge>
              </div>
            </custom-table-cell>
            <!-- Позиций и ближайшая отгрузка -->
            <custom-table-cell align="center">
              <div 
                class="items-shipping-cell" 
                :title="'Количество позиций: ' + props.row.items_count + (props.row.nearest_shipping_date ? '\nБлижайшая отгрузка: ' + formatDateWithDay(props.row.nearest_shipping_date) : '')"
              >
                <span class="items-count">{{ props.row.items_count }}</span>
                <span v-if="props.row.nearest_shipping_date" class="nearest-shipping">
                  {{ formatDateWithDay(props.row.nearest_shipping_date) }}
                </span>
              </div>
            </custom-table-cell>
            <!-- Сумма -->
            <custom-table-cell align="right">
              <span v-if="props.row.is_show_sum_price" class="amount" :title="'Общая сумма заявки: ' + formatAmount(props.row.items_total)">{{ formatAmount(props.row.items_total) }}</span>
              <span v-else>—</span>
            </custom-table-cell>
            <!-- Накладные (объединённая колонка) -->
            <custom-table-cell align="center">
              <div 
                class="invoice-combined" 
                :title="getInvoiceTooltip(props.row)"
              >
                <!-- Если есть накладные -->
                <div v-if="props.row.invoices_json && props.row.invoices_json.length" class="invoices-list-col">
                  <div
                    v-for="inv in props.row.invoices_json"
                    :key="inv.id"
                    :class="['invoice-item-block tw-pointer', { 'invoice-item-block--unsigned': !inv.is_provider_signed, 'invoice-item-block--manual': inv.is_manual_signed, 'invoice-item-block--pending': inv.is_pending_approval, 'invoice-item-block--rejected': inv.is_rejected_approval }]"
                    @click.stop="openViewInvoice(inv, props.row.provider_request_id)"
                  >
                    <div class="invoice-num-line">
                      <q-icon v-if="inv.is_manual_signed" name="upload_file" size="12px" color="amber-9" class="q-mr-xs">
                        <q-tooltip>Вручную загруженная накладная</q-tooltip>
                      </q-icon>
                      №{{ inv.num }}
                      <q-icon v-if="!inv.is_provider_signed" name="edit_off" size="12px" color="orange-8" class="q-ml-xs">
                        <q-tooltip>Не подписана поставщиком</q-tooltip>
                      </q-icon>
                    </div>
                    <div class="invoice-date-sum-line">
                      <span class="invoice-date">от {{ inv.date }}</span>
                      <div>
                        <q-badge v-if="inv.is_rejected_approval" color="negative" text-color="white" class="q-mr-xs">
                          <q-icon name="block" size="11px" class="q-mr-xs" />
                          Отклонено
                          <q-tooltip>Утверждение цен отклонено. Отредактируйте накладную (цена и количество) в карточке либо удалите её и создайте заново.</q-tooltip>
                        </q-badge>
                        <q-badge v-else-if="inv.is_pending_approval" color="amber-8" text-color="white" class="q-mr-xs">
                          <q-icon name="hourglass_empty" size="11px" class="q-mr-xs" />
                          На утверждении
                          <q-tooltip>Накладная ожидает утверждения цены</q-tooltip>
                        </q-badge>
                        <span v-else class="invoice-sum">({{ formatAmountShort(inv.sum) }})</span>

                        <span v-if="isShowAll && inv.is_send_to_1c === 2"
                          class="tw-self-start tw-block tw-w-fit tw-text-[10px] tw-text-[#fff] tw-text-bold tw-bg-[green] tw-rounded-md tw-p-[2px]">
                          1С
                          <q-tooltip>Отправлено в 1С</q-tooltip>
                        </span>
                      </div>
                    </div>
                    </div>
                  <!-- Показываем сумму без накладной если есть -->
                  <div v-if="props.row.not_invoiced_sum > 0 && props.row.is_show_sum_price" class="not-invoiced-row">
                    Без накл: {{ formatAmountShort(props.row.not_invoiced_sum) }}
                  </div>
                </div>
                <!-- Если нет накладных -->
                <div v-else>
                  <q-badge
                    v-if="props.row.not_invoiced_sum > 0 && props.row.is_show_sum_price"
                    color="orange"
                    :label="'Нет (' + formatAmountShort(props.row.not_invoiced_sum) + ')'"
                  />
                  <q-badge v-else color="grey" label="Нет" />
                </div>
              </div>
            </custom-table-cell>
            <!-- Реализации -->
            <custom-table-cell align="center">
              <div class="realizes-combined">
                <q-spinner 
                  v-if="loadingRealizes[props.row.provider_request_id]" 
                  color="primary" 
                  size="16px" 
                />
                <div v-else-if="realizesData[props.row.provider_request_id]?.length" class="realizes-list-col">
                  <div
                    v-for="real in realizesData[props.row.provider_request_id]"
                    :key="real.realize_id"
                    class="realize-item-block"
                    @click.stop="openViewRealize(real)"
                  >
                    <div class="realize-num-line">№{{ real.realize_num }}</div>
                    <div class="realize-date-sum-line">
                      <span class="realize-date">от {{ real.realize_date }}</span>
                      <span class="realize-sum">({{ formatAmountShort(real.realize_sum) }})</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="realizesData[props.row.provider_request_id] !== undefined">
                  <q-badge color="grey" label="Нет" />
                </div>
                <div v-else>
                  <q-btn 
                    flat 
                    dense 
                    size="sm" 
                    icon="refresh" 
                    color="primary"
                    title="Загрузить реализации"
                    @click.stop="loadRealizes(props.row.provider_request_id)"
                  />
                </div>
              </div>
            </custom-table-cell>
            <!-- Expand -->
            <custom-table-cell align="center">
              <q-icon 
                :name="expandedRows[props.row.provider_request_id] ? 'expand_less' : 'expand_more'" 
                size="sm"
                class="expand-icon"
                :title="expandedRows[props.row.provider_request_id] ? 'Свернуть' : 'Развернуть'"
              />
            </custom-table-cell>
          </q-tr>

          <!-- Раскрывающиеся детали -->
          <q-tr v-if="!props.row._isGroupHeader && expandedRows[props.row.provider_request_id]" class="details-row">
            <td :colspan="this.requestsColumnsList.length">
              <div class="details-container">
                <div class="details-header">
                  <div class="details-title-row">
                    <h4>Заявка #{{ props.row.provider_request_id }} — {{ props.row.provider_name }}</h4>
                    <div v-if="showActions" class="details-actions">
                      <q-btn
                        color="white"
                        text-color="primary"
                        icon="add"
                        label="Добавить материал"
                        size="sm"
                        @click.stop="openAddMaterial(props.row)"
                      />
                      <q-btn
                        color="white"
                        text-color="primary"
                        icon="receipt"
                        label="Создать накладную"
                        size="sm"
                        :disable="!props.row.can_create_invoice"
                        :title="getCreateInvoiceTitle(props.row)"
                        @click.stop="openCreateInvoice(props.row)"
                      />
                      <q-btn
                        v-if="props.row.is_show_add_manual_invoice"
                        color="white"
                        text-color="primary"
                        icon="receipt"
                        label="Загрузить подписанную накладную"
                        size="sm"
                        @click.stop="openUploadInvoice(props.row)"
                      />
                    </div>
                  </div>
                  <div class="details-info">
                    <span><strong>Заказчик материала:</strong> {{ props.row.contractor_name || 'Не указан' }}</span>
                    <span v-if="getInitiatorText(props.row)">
                      <strong>Инициатор заявки на материал:</strong> {{ getInitiatorText(props.row) }}
                    </span>
                    <span v-if="props.row.warehouse_name"><strong>Склад:</strong> {{ props.row.warehouse_name }}</span>
                    <span v-if="props.row.is_self_delivery"><q-badge color="info" label="Самовывоз" /></span>
                  </div>
                </div>
                
                <div v-if="loadingItems[props.row.provider_request_id]" class="loading-items">
                  <q-spinner color="primary" size="2em" />
                  <span>Загрузка позиций...</span>
                </div>
                
                <q-table 
                  v-else-if="itemsData[props.row.provider_request_id]?.length"
                  :rows="itemsData[props.row.provider_request_id]"
                  :columns="itemsColumns"
                  row-key="provider_request_item_id"
                  flat
                  dense
                  class="items-table"
                  :pagination="{ rowsPerPage: 0 }"
                  hide-pagination
                >
                  <template v-slot:body-cell-material_name="props">
                    <q-td :props="props">
                      <div class="material-info" :title="props.row.material_name">
                        <span class="material-name">{{ props.row.material_name }}</span>
                        <span v-if="props.row.material_code" class="material-code">{{ props.row.material_code }}</span>
                        <div class="provider-meta">
                          <div class="provider-block" :class="{ 'provider-block--identical': isProviderNameIdentical(props.row) }">
                            <div class="provider-head">
                              <span class="provider-label">Название поставщика</span>
                              <q-badge
                                v-if="isProviderNameIdentical(props.row)"
                                color="green-7"
                                text-color="white"
                                class="provider-badge"
                                label="Идентично"
                              />
                            </div>
                            <div v-if="!isProviderNameIdentical(props.row)" class="provider-value provider-value--provider">
                              {{ props.row.provider_material_name || 'не указано' }}
                            </div>
                          </div>
                          <div class="provider-block">
                            <div class="provider-head">
                              <span class="provider-label">Номенклатурный номер поставщика</span>
                            </div>
                            <div class="provider-value">
                              {{ props.row.provider_nomenklatur_num || 'не указан' }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-date_shipping="props">
                    <q-td :props="props" class="text-center" :title="'Дата отгрузки: ' + formatDateWithDay(props.row.date_shipping)">
                      {{ formatDateWithDay(props.row.date_shipping) }}
                    </q-td>
                  </template>
                  <template v-slot:body-cell-material_cnt="props">
                    <q-td :props="props" class="text-center">
                      <span>{{ props.row.material_cnt }}</span>
                      <span class="unit-name">{{ props.row.material_unit_name }}</span>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-price="props">
                    <q-td :props="props" class="text-right">
                      {{ props.row.is_show_sum_price ? formatAmount(props.row.price) : '—' }}
                    </q-td>
                  </template>
                  <template v-slot:body-cell-item_sum="props">
                    <q-td :props="props" class="text-right">
                      <strong>{{ props.row.is_show_sum_price ? formatAmount(props.row.item_sum) : '—' }}</strong>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-provider_request_status_name="props">
                    <q-td :props="props" class="text-center">
                      <div class="status-cell">
                        <span v-if="isShowAll" class="tw-block tw-text-[12px] tw-font-medium tw-flex-1" :style="`color: ${getStatusColor(props.row.provider_request_status_id, true)};`">{{ props.row.provider_request_status_name }}</span>
                        <!-- Badge статуса с dropdown -->
                        <!-- Статусы 200, 250, 300, 350 - автоматические (накладные), 700 - отменён -->
                        <q-btn-dropdown
                          v-else
                          :color="getStatusColor(props.row.provider_request_status_id)"
                          :label="props.row.provider_request_status_name"
                          dense
                          flat
                          no-caps
                          size="sm"
                          class="status-dropdown"
                          :disable="isAutoStatus(props.row.provider_request_status_id)"
                        >
                          <q-list dense>
                            <q-item 
                              v-if="canChangeToStatus(props.row, 1000)"
                              clickable 
                              v-close-popup 
                              @click="changeItemStatusTo(props.row, 1000)"
                            >
                              <q-item-section>
                                <q-badge color="orange" label="Нет в наличии" />
                              </q-item-section>
                            </q-item>
                            <q-item 
                              v-if="canChangeToStatus(props.row, 700)"
                              clickable 
                              v-close-popup 
                              @click="changeItemStatusTo(props.row, 700)"
                            >
                              <q-item-section>
                                <q-badge color="red" label="Отменен" />
                              </q-item-section>
                            </q-item>
                          </q-list>
                        </q-btn-dropdown>
                        <span v-if="isShowAll && props.row.provider_request_status_code === 'SHIP_PARTIAL'"
                              class="tw-block tw-text-[11px] tw-text-[#626262] tw-flex-1">{{ `Отгружено ${props.row.invoiced_cnt} ${props.row.material_unit_name}` }}</span>
                      </div>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-invoices_info="props">
                    <q-td :props="props" class="text-left">
                      <div v-if="props.row.invoices_info" class="invoices-cell">
                        <q-icon name="receipt" size="14px" color="green" class="q-mr-xs" />
                        <span class="invoices-text" :title="props.row.invoices_info">
                          {{ props.row.invoices_info }}
                        </span>
                      </div>
                      <span v-else class="no-data">-</span>
                    </q-td>
                  </template>

                  <template v-slot:body-cell-certificate_urls="props">
                    <q-td :props="props" class="text-center">
                      <div class="certificates__actions">
                        <!-- Match Projects view: show certificate number pill (edit), download link and delete icon, or Add link -->
                        <template v-if="props.row.project_material_certificate_id || props.row.document_url || (props.row.certificate_urls && props.row.certificate_urls.length)">
                          <Clickable
                            v-if="props.row.certificate_number && showActions"
                            class="certificates__cert-number"
                            @click.stop="() => this.openViewCertificate(props.row)"
                          >
                            {{ props.row.certificate_number }}
                            <CustomIcon icon="edit" small style="margin-left: 4px;" />
                            <q-tooltip>Редактировать номер сертификата</q-tooltip>
                          </Clickable>

                          <div class="certificates__center-wrap">
                            <Clickable
                              v-if="props.row.document_url || (props.row.certificate_urls && props.row.certificate_urls.length)"
                              class="certificates__download-link"
                              :link="true"
                              :href="`${API_URL}${props.row.document_url || (props.row.certificate_urls && props.row.certificate_urls[0])}`"
                              target="_blank"
                            >
                              Скачать
                            </Clickable>

                            <q-btn
                              v-if="!!props.row.project_material_certificate_id && showActions"
                              flat
                              dense
                              round
                              size="xs"
                              color="negative"
                              class="q-ml-xs"
                              @click.stop="confirmDeleteCertificate(props.row)"
                              title="Удалить сертификат"
                            >
                              <q-icon name="delete" />
                            </q-btn>
                          </div>
                        </template>

                        <template v-else-if="showActions">
                          <Clickable
                            class="certificates__add-link"
                            @click.stop="() => this.openUploadModal(props.row)"
                          >
                            Добавить сертификат
                          </Clickable>
                        </template>
                      </div>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-actions="props" v-if="showActions">
                    <q-td :props="props" class="text-center">
                      <q-btn
                        v-if="props.row.material_code === 'PODDON'"
                        flat
                        dense
                        size="sm"
                        icon="delete"
                        color="red-5"
                        :disable="props.row.provider_request_status_id !== 100"
                        :title="props.row.provider_request_status_id !== 100 ? 'Удаление доступно только в статусе Создан' : 'Удалить позицию'"
                        @click.stop="confirmDeletePoddonItem(props.row.provider_request_id, props.row)"
                      />
                    </q-td>
                  </template>
                </q-table>
                
                <div v-else class="no-items">
                  Нет позиций в заявке
                </div>
              </div>
            </td>
          </q-tr>
        </template>
      </CustomTable>

      <!-- Плавающая панель при мульти-выборе -->
      <transition name="slide-up" v-if="showActions">
        <div v-if="selectedRequestsCount > 0" class="selection-bar">
          <div class="selection-bar-content">
            <div class="selection-info">
              <q-icon name="check_circle" size="22px" class="q-mr-sm" />
              Выбрано заявок: <strong>{{ selectedRequestsCount }}</strong>
            </div>
            <div class="selection-actions">
              <q-btn
                color="white"
                text-color="primary"
                icon="receipt"
                label="Создать общую накладную"
                unelevated
                @click="openCreateInvoiceForSelected"
              />
              <q-btn
                color="amber-9"
                text-color="white"
                icon="upload_file"
                label="Загрузить общую вручную"
                unelevated
                @click="openUploadInvoiceForSelected"
              />
              <q-btn
                flat
                color="white"
                icon="close"
                label="Снять выбор"
                @click="clearSelection"
              />
            </div>
          </div>
        </div>
      </transition>

      <!-- Пагинация -->
      <div class="pagination-section" v-if="totalCount > filters.limit">
        <q-pagination
          v-model="filters.page"
          :max="Math.ceil(totalCount / filters.limit)"
          direction-links
          boundary-links
          @update:model-value="applyFilters"
        />
        <span class="total-info">
          Показано: {{ filteredRowsData.length }} 
          <span v-if="quickFilter !== 'all'">из {{ rowsData.length }}</span>
          <span v-if="totalCount > rowsData.length"> (всего в БД: {{ totalCount }})</span>
        </span>
      </div>
    </div>

    <!-- Диалог выбора SBS-проекта -->
    <q-dialog v-model="sbsProjectDialog.show" persistent>
      <q-card style="min-width: 700px; max-width: 900px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор SBS-проекта</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="sbsProjectDialog.show = false" />
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="sbsProjectSearch"
            outlined
            dense
            placeholder="Поиск по названию или коду..."
            clearable
            autofocus
            @update:model-value="onSbsSearchInput"
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <div style="margin-top: 16px;">
            <q-linear-progress v-if="isLoadingSbsProjects" indeterminate />

            <div v-else-if="sbsProjectList.length === 0" style="text-align: center; padding: 40px; color: #999;">
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top: 12px;">Проекты не найдены</div>
            </div>

            <template v-else>
              <q-table
                :rows="sbsProjectList"
                :columns="[
                  { name: 'sbs_project_id', label: 'ID', field: 'sbs_project_id', align: 'center', style: 'width: 80px' },
                  { name: 'naimenovanie', label: 'Наименование', field: 'naimenovanie', align: 'left' },
                  { name: 'kod', label: 'Код', field: 'kod', align: 'left', style: 'width: 120px' },
                  { name: 'actions', label: '', field: 'actions', align: 'center', style: 'width: 100px' },
                ]"
                row-key="sbs_project_id"
                flat
                bordered
                hide-bottom
                :rows-per-page-options="[0]"
                style="max-height: 400px; overflow-y: auto;"
              >
                <template v-slot:body-cell-actions="props">
                  <q-td :props="props">
                    <q-btn
                      label="Выбрать"
                      color="primary"
                      size="sm"
                      unelevated
                      @click="selectSbsProject(props.row)"
                    />
                  </q-td>
                </template>
              </q-table>

              <div style="font-size: 13px; color: #666; margin-top: 8px; padding: 0 4px;">
                Показано {{ sbsProjectList.length }}
                <span v-if="sbsProjectTotalCount > sbsProjectList.length"> из {{ sbsProjectTotalCount }} — уточните поиск для нужного результата</span>
              </div>
            </template>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="sbsProjectDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import UploadExcelBtn from "@/components/common/UploadExcelBtn";
import CustomDate from "@/components/form/CustomDate";
import CustomSelect from "@/components/form/CustomSelect";
import CustomIcon from "@/components/common/CustomIcon.vue";
import Clickable from '@/components/common/Clickable';
import { useAppStore } from "@/services/store/app";
import { MODAL_NAMES_AIDAR } from "@/constants/index_aidar";
import { API_URL, MODAL_NAMES } from "@/constants";
import { formatPhoneNumber } from "@/services";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { deleteProjectMaterialCertificate } from "@/views/ResidentialSettings/services";
import { 
  getSupplierRequests, 
  getSupplierRequestItems,
  deleteSupplierRequestItem,
  exportSupplierRequestMaterials,
  getStatuses, 
  getPurchaseTypes, 
  getInitiatorTypes,
  changeItemStatus,
  getRealizes,
  requestsColumns,
  itemsColumns,
  getSbsProjects,
  getRequiredTypes,
} from "@/views/SupplierRequests/services";
import { getProviders } from "@/views/Materials/services";

export default {
  name: "SupplierRequests",
  components: { 
    CustomTable, 
    CustomTableCell, 
    UploadExcelBtn,
    CustomDate, 
    CustomSelect,
    CustomIcon,
    Clickable,
  },
  data() {
    return {
      itemsColumns,
      API_URL,
      rowsData: [],
      isFetching: false,
      isExporting: false,
      totalCount: 0,
      isShowAll: false,
      listSortMode: 'shipping_grouped',
      listSortOptions: [
        { value: 'shipping_grouped', label: 'По дате отгрузки' },
        { value: 'created_flat', label: 'По дате создания заявки' },
      ],      // Мульти-выбор заявок для общей накладной
      selectedRequests: {},
      // Фильтры
      filters: {
        date_shipping_begin: null,
        date_shipping_end: null,
        date_create_begin: null,
        date_create_end: null,
        resident_id: null,
        project_type_id: null,
        sbs_project_id: null,
        required_type_id: null,
        provider_request_status_id_arr: [],
        provider_request_initiator_type_id: null,
        provider_request_id: null,
        invoice_num: null,
        provider_request_invoice_id: null,
        material_id: null,
        invoice_filter_type: null,
        send_to_1c_filter: null,
        provider_id: null,
        page: 1,
        limit: 50
      },

      providerFilterOptions: [],
      
      // Выбранный проект для отображения
      selectedProject: null,
      
      // Выбранный материал для отображения
      selectedMaterial: null,

      // SBS-проект: модалка выбора
      selectedSbsProject: null,
      sbsProjectDialog: { show: false },
      sbsProjectList: [],
      sbsProjectSearch: '',
      sbsProjectPage: 1,
      sbsProjectLimit: 20,
      sbsProjectTotalCount: 0,
      isLoadingSbsProjects: false,
      sbsSearchDebounce: null,

      // Типы требований для дропдауна
      requiredTypes: [],
      
      // Фильтр по накладным
      invoiceFilterOptions: [
        { label: 'Все', value: null },
        { label: 'Не подписанные накладные', value: 'UNSIGNED' },
        { label: 'Нет накладных', value: 'NO_INVOICE' },
      ],

      sendTo1cFilterOptions: [
        { label: 'Все', value: null },
        { label: 'Отправлена в 1С', value: 'SENT' },
        { label: 'Не отправлена в 1С', value: 'NOT_SENT' },
      ],
      
      // Доступные статусы с цветами
      availableStatuses: [
        { provider_request_status_id: 100, provider_request_status_name: 'Создан', color: 'grey' },
        { provider_request_status_id: 200, provider_request_status_name: 'Есть в наличии', color: 'green' },
        { provider_request_status_id: 250, provider_request_status_name: 'Частично есть накладная', color: 'blue' },
        { provider_request_status_id: 350, provider_request_status_name: 'Частично отгружен', color: 'light-blue' },
        { provider_request_status_id: 300, provider_request_status_name: 'Отгружен', color: 'light-green' },
        { provider_request_status_id: 700, provider_request_status_name: 'Отменен', color: 'red' },
        { provider_request_status_id: 1000, provider_request_status_name: 'Нет в наличии', color: 'orange' },
      ],
      
      // Справочники
      statuses: [],
      purchaseTypes: [],
      initiatorTypes: [],
      
      // Раскрытые строки
      expandedRows: {},
      itemsData: {},
      loadingItems: {},
      deleting: {},
      
      // Реализации
      realizesData: {},
      loadingRealizes: {},
      
      // Debounce timer
      debounceTimer: null,
      isInitialLoad: true,
      
      // Быстрый фильтр: all, active, overdue, completed
      quickFilter: 'all',
      
      // Видимость секции фильтров
      filtersVisible: true,
    };
  },
  computed: {
    selectedProjectLabel() {
      if (this.selectedProject) {
        return `${this.selectedProject.resident_name} - ${this.selectedProject.project_type_name}`;
      }
      return '';
    },
    selectedSbsProjectLabel() {
      if (this.selectedSbsProject) {
        return this.selectedSbsProject.naimenovanie || `SBS #${this.selectedSbsProject.sbs_project_id}`;
      }
      return '';
    },
    selectedMaterialLabel() {
      if (this.selectedMaterial) {
        return this.selectedMaterial.material_name;
      }
      return '';
    },
    
    // Краткая информация о текущих фильтрах (когда свёрнуто)
    getFiltersSummary() {
      const parts = [];
      
      if (this.filters.date_shipping_begin || this.filters.date_shipping_end) {
        const from = this.filters.date_shipping_begin ? this.formatDateSafe(this.filters.date_shipping_begin) : '...';
        const to = this.filters.date_shipping_end ? this.formatDateSafe(this.filters.date_shipping_end) : '...';
        parts.push(`📅 отгрузка: ${from} — ${to}`);
      }
      if (this.filters.date_create_begin || this.filters.date_create_end) {
        const from = this.filters.date_create_begin ? this.formatDateSafe(this.filters.date_create_begin) : '...';
        const to = this.filters.date_create_end ? this.formatDateSafe(this.filters.date_create_end) : '...';
        parts.push(`📝 создание: ${from} — ${to}`);
      }
      
      if (this.filters.provider_request_status_id_arr?.length) {
        parts.push(`📋 ${this.filters.provider_request_status_id_arr.length} стат.`);
      }
      
      if (this.selectedProject) {
        parts.push(`🏢 ${this.selectedProject.resident_name}`);
      }
      
      if (this.selectedSbsProject) {
        const name = this.selectedSbsProject.naimenovanie || '';
        parts.push(`SBS: ${name.length > 20 ? name.substring(0, 20) + '...' : name}`);
      }

      if (this.filters.required_type_id) {
        const rt = this.requiredTypes.find(x => x.required_type_id === this.filters.required_type_id);
        parts.push(`Тип: ${rt ? rt.required_type_name : this.filters.required_type_id}`);
      }

      if (this.selectedMaterial) {
        const name = this.selectedMaterial.material_name;
        parts.push(`📦 ${name.length > 15 ? name.substring(0, 15) + '...' : name}`);
      }

      if (this.filters.send_to_1c_filter === 'SENT') {
        parts.push('накл. в 1С: отправлено');
      } else if (this.filters.send_to_1c_filter === 'NOT_SENT') {
        parts.push('накл. в 1С: не отправлено');
      }

      if (this.isShowAll && this.filters.provider_id) {
        const p = this.providerFilterOptions.find((x) => x.provider_id === this.filters.provider_id);
        parts.push(p ? `Поставщик: ${p.provider_name}` : `Поставщик #${this.filters.provider_id}`);
      }
      
      return parts.length ? parts.join(' • ') : 'Фильтры не применены';
    },
    
    selectedRequestsCount() {
      return Object.keys(this.selectedRequests).length;
    },
    selectedRequestsList() {
      return Object.values(this.selectedRequests);
    },
    
    // Определение типа заявки: active, overdue, completed
    categorizedRows() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return this.rowsData.map(row => {
        // Проверяем завершённость — все позиции отгружены
        const isCompleted = this.isRequestCompleted(row);
        
        // Проверяем просроченность — есть дата отгрузки и она в прошлом
        let isOverdue = false;
        if (!isCompleted && row.nearest_shipping_date) {
          const shippingDate = new Date(row.nearest_shipping_date);
          shippingDate.setHours(0, 0, 0, 0);
          isOverdue = shippingDate < today;
        }
        
        // Активные — не завершены и не просрочены
        const isActive = !isCompleted && !isOverdue;
        
        return {
          ...row,
          _category: isCompleted ? 'completed' : (isOverdue ? 'overdue' : 'active')
        };
      });
    },
    
    // Подсчёт по категориям
    activeCount() {
      return this.categorizedRows.filter(r => r._category === 'active').length;
    },
    overdueCount() {
      return this.categorizedRows.filter(r => r._category === 'overdue').length;
    },
    completedCount() {
      return this.categorizedRows.filter(r => r._category === 'completed').length;
    },
    
    // Опции для быстрого фильтра с количеством
    quickFilterOptions() {
      return [
        { label: `Все (${this.rowsData.length})`, value: 'all', icon: 'list' },
        { label: `Активные (${this.activeCount})`, value: 'active', icon: 'schedule' },
        { label: `Просроч. (${this.overdueCount})`, value: 'overdue', icon: 'warning' },
        { label: `Заверш. (${this.completedCount})`, value: 'completed', icon: 'check_circle' }
      ];
    },
    
    // Отфильтрованные данные по quickFilter
    filteredRowsData() {
      if (this.quickFilter === 'all') {
        return this.categorizedRows;
      }
      return this.categorizedRows.filter(r => r._category === this.quickFilter);
    },
    
    // Строки таблицы: группировка по отгрузке или плоский список по дате создания
    groupedRowsData() {
      if (!this.filteredRowsData || !this.filteredRowsData.length) return [];
      if (this.listSortMode === 'created_flat') {
        return this.buildCreatedDateFlatRows();
      }
      return this.buildShippingGroupedRows();
    },
    showActions() {
      if(!this.isShowAll) return true;
      return this.isShowAllActions && this.isShowAll;
    },
    requestsColumnsList() {
      if (this.showActions) {
        return [
          { name: 'select_request', label: '✓', align: 'center', style: 'width: 40px' },
          ...requestsColumns
        ];
      }
      return requestsColumns;
    }
  },
  watch: {
    // Синхронизация фильтров с URL и автоматическая фильтрация
    filters: {
      handler() {
        this.updateUrlParams();
        if (!this.isInitialLoad) {
          this.debouncedGetData();
        }
      },
      deep: true
    },
    // При поиске по ID заявки — сбрасываем остальные фильтры
    'filters.provider_request_id'(newVal) {
      if (newVal && !this.isInitialLoad) {
        this.clearOtherFilters('provider_request_id');
      }
    },
    // При поиске по номеру накладной — сбрасываем остальные фильтры
    'filters.invoice_num'(newVal) {
      if (newVal && !this.isInitialLoad) {
        this.clearOtherFilters('invoice_num');
      }
    },
    // При поиске по ID накладной — сбрасываем остальные фильтры
    'filters.provider_request_invoice_id'(newVal) {
      if (newVal && !this.isInitialLoad) {
        this.clearOtherFilters('provider_request_invoice_id');
      }
    }
  },
  methods: {
    getRequestTableRowKey(row) {
      if (row._isGroupHeader) {
        return `gh-${row._groupDate ?? 'nodate'}`;
      }
      return row.provider_request_id;
    },
    /** timestamp для сортировки по date_send (создание заявки) */
    rowDateSendTime(row) {
      const v = row?.date_send;
      if (v == null || v === '') return null;
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : null;
    },
    buildShippingGroupedRows() {
      const groups = {};
      const noDateKey = '__no_date__';

      this.filteredRowsData.forEach((row) => {
        const dateKey = row.nearest_shipping_date
          ? this.formatDateForInput(row.nearest_shipping_date)
          : noDateKey;

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(row);
      });

      const sortedKeys = Object.keys(groups).sort((a, b) => {
        if (a === noDateKey) return 1;
        if (b === noDateKey) return 1;
        return a.localeCompare(b);
      });

      const result = [];
      sortedKeys.forEach((dateKey) => {
        result.push({
          _isGroupHeader: true,
          _groupDate: dateKey === noDateKey ? null : dateKey,
          _groupLabel:
            dateKey === noDateKey
              ? 'Без даты отгрузки'
              : this.formatDateWithDayFull(dateKey),
          _groupCount: groups[dateKey].length,
          _groupSum: groups[dateKey].reduce((sum, r) => sum + (r.items_total || 0), 0),
        });

        groups[dateKey].forEach((row) => {
          result.push(row);
        });
      });

      return result;
    },
    buildCreatedDateFlatRows() {
      const rows = [...this.filteredRowsData];
      rows.sort((a, b) => {
        const ta = this.rowDateSendTime(a);
        const tb = this.rowDateSendTime(b);
        if (ta != null && tb != null && ta !== tb) {
          return tb - ta;
        }
        if (ta != null && tb == null) return -1;
        if (ta == null && tb != null) return 1;
        return (b.provider_request_id || 0) - (a.provider_request_id || 0);
      });
      return rows;
    },
    buildRequestParams({ includePagination = true } = {}) {
      const params = {};

      if (includePagination) {
        params.page = this.filters.page;
        params.limit = this.filters.limit;
      }

      if (this.filters.date_shipping_begin) {
        params.date_shipping_begin = this.filters.date_shipping_begin;
      }
      if (this.filters.date_shipping_end) {
        params.date_shipping_end = this.filters.date_shipping_end;
      }
      if (this.filters.date_create_begin) {
        params.date_create_begin = this.filters.date_create_begin;
      }
      if (this.filters.date_create_end) {
        params.date_create_end = this.filters.date_create_end;
      }
      if (this.filters.resident_id) {
        params.resident_id = this.filters.resident_id;
      }
      if (this.filters.project_type_id) {
        params.project_type_id = this.filters.project_type_id;
      }
      if (this.filters.provider_request_status_id_arr?.length) {
        params.provider_request_status_id_arr = this.filters.provider_request_status_id_arr.join(',');
      }
      if (this.filters.provider_request_initiator_type_id) {
        params.provider_request_initiator_type_id = this.filters.provider_request_initiator_type_id;
      }
      if (this.filters.provider_request_id) {
        params.provider_request_id = this.filters.provider_request_id;
      }
      if (this.filters.invoice_num) {
        params.invoice_num = this.filters.invoice_num;
      }
      if (this.filters.provider_request_invoice_id) {
        params.provider_request_invoice_id = this.filters.provider_request_invoice_id;
      }
      if (this.filters.material_id) {
        params.material_id = this.filters.material_id;
      }
      if (this.filters.invoice_filter_type === 'UNSIGNED') {
        params.has_unsigned_provider_invoice = true;
      } else if (this.filters.invoice_filter_type === 'NO_INVOICE') {
        params.show_no_invoice = true;
      }
      if (this.filters.send_to_1c_filter) {
        params.send_to_1c_filter = this.filters.send_to_1c_filter;
      }
      if (this.filters.provider_id) {
        params.provider_id = this.filters.provider_id;
      }
      if (this.filters.sbs_project_id) {
        params.sbs_project_id = this.filters.sbs_project_id;
      }
      if (this.filters.required_type_id) {
        params.required_type_id = this.filters.required_type_id;
      }

      return params;
    },
    normalizeText(value) {
      return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
    },
    isProviderNameIdentical(row) {
      const a = this.normalizeText(row?.material_name);
      const b = this.normalizeText(row?.provider_material_name);
      return !!a && !!b && a === b;
    },
    // Сброс всех фильтров кроме указанного (для поиска по ID)
    clearOtherFilters(keepField) {
      const searchFields = ['provider_request_id', 'invoice_num', 'provider_request_invoice_id'];
      
      // Сбрасываем даты
      this.filters.date_shipping_begin = null;
      this.filters.date_shipping_end = null;
      this.filters.date_create_begin = null;
      this.filters.date_create_end = null;
      
      // Сбрасываем остальные фильтры
      this.filters.resident_id = null;
      this.filters.project_type_id = null;
      this.filters.sbs_project_id = null;
      this.filters.required_type_id = null;
      this.filters.provider_request_status_id_arr = [];
      this.filters.provider_request_initiator_type_id = null;
      this.filters.material_id = null;
      this.filters.invoice_filter_type = null;
      this.filters.send_to_1c_filter = null;
      this.filters.provider_id = null;
      this.selectedProject = null;
      this.selectedSbsProject = null;
      this.selectedMaterial = null;
      
      // Сбрасываем другие поля поиска (кроме текущего)
      searchFields.forEach(field => {
        if (field !== keepField) {
          this.filters[field] = null;
        }
      });
      
      // Сбрасываем быстрый фильтр
      this.quickFilter = 'all';
    },
    
    // Переключение видимости фильтров с сохранением в localStorage
    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
      localStorage.setItem('supplierRequestsFiltersVisible', JSON.stringify(this.filtersVisible));
    },
    
    // Загрузка настроек из localStorage
    loadSettings() {
      const saved = localStorage.getItem('supplierRequestsFiltersVisible');
      if (saved !== null) {
        this.filtersVisible = JSON.parse(saved);
      }
    },
    
    formatDateForInput(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('ru-RU');
    },
    requestCreatedTitle(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return `Создано: ${d.toLocaleString('ru-RU')}`;
    },
    
    // Безопасное форматирование даты (для YYYY-MM-DD)
    formatDateSafe(dateStr) {
      if (!dateStr) return '-';
      // Если формат YYYY-MM-DD, парсим вручную
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('ru-RU');
    },
    
    formatDateWithDay(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
      const dayOfWeek = days[d.getDay()];
      const dateFormatted = d.toLocaleDateString('ru-RU');
      return `${dateFormatted} (${dayOfWeek})`;
    },
    
    formatDateWithDayFull(dateStr) {
      if (!dateStr) return 'Без даты';
      const d = new Date(dateStr);
      const daysLong = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
      const dayOfWeek = daysLong[d.getDay()];
      
      // Определяем "сегодня", "завтра", "вчера"
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateCheck = new Date(d);
      dateCheck.setHours(0, 0, 0, 0);
      
      const diffDays = Math.round((dateCheck - today) / (1000 * 60 * 60 * 24));
      
      let prefix = '';
      if (diffDays === 0) prefix = '📅 Сегодня — ';
      else if (diffDays === 1) prefix = '📆 Завтра — ';
      else if (diffDays === -1) prefix = '📋 Вчера — ';
      else if (diffDays < -1) prefix = '⏮️ ';
      else prefix = '📆 ';
      
      const dateFormatted = d.toLocaleDateString('ru-RU', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
      
      return `${prefix}${dateFormatted} (${dayOfWeek})`;
    },
    
    formatAmount(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('ru-RU', { 
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value) + ' ₸';
    },
    
    getStatusColor(statusId, isStock = false) {
      const colors = {
        100: 'grey',      // Создан
        200: isStock ? '#4472b9' : 'green',     // Есть в наличии
        250: 'blue', // Частично есть в наличии
        300: isStock ? 'green' :'light-green',      // Отгружен
        350: isStock ? 'blue' : 'light-blue', // Частично отгружен
        700: 'red',       // Отменен
        1000: 'orange',   // Нет в наличии
      };
      console.log(colors[statusId] || 'grey');
      return colors[statusId] || 'grey';
    },
    
    // Проверка: является ли статус автоматическим (нельзя менять вручную)
    isAutoStatus(statusId) {
      // 200 - Есть в наличии, 250 - Частично есть в наличии
      // 300 - Отгружен, 350 - Частично отгружен
      // 700 - Отменен (финальный статус)
      const autoStatuses = [200, 250, 300, 350, 700];
      return autoStatuses.includes(statusId);
    },
    
    // Проверка: все ли позиции отгружены (или отменены)
    isRequestCompleted(row) {
      // Парсим статусы из provider_request_status_name
      // Формат: "Отгружен (5), Отменен (1)"
      if (!row.provider_request_status_name || row.provider_request_status_name === 'Нет позиций') {
        return false;
      }
      
      const parts = row.provider_request_status_name.split(', ');
      
      // Считаем все позиции и "завершённые" (Отгружен + Отменен)
      let totalCount = 0;
      let completedCount = 0;
      
      parts.forEach(part => {
        const match = part.match(/^(.+?)\s*\((\d+)\)$/);
        if (match) {
          const name = match[1].trim();
          const count = parseInt(match[2], 10);
          totalCount += count;
          
          // "Отгружен" или "Отменен" считаем завершёнными
          if (name === 'Отгружен' || name === 'Отменен') {
            completedCount += count;
          }
        }
      });
      
      return totalCount > 0 && completedCount === totalCount;
    },
    
    getInitiatorShort(initiatorTypeId) {
      // 1 = Команда проекта, 2 = Подрядчик
      return initiatorTypeId === 1 ? 'КП' : 'ПР';
    },

    getInitiatorText(row) {
      if (!row) return '';

      const typeName = row.provider_request_initiator_type_name;
      const fio = row.initiator_fio;
      const phone = row.initiator_phone ? formatPhoneNumber(row.initiator_phone) : '';

      if (!typeName && !fio) return '';

      if (fio) {
        const personInfo = phone ? `${fio}. Телефон: ${phone}` : fio;
        return typeName ? `${typeName} (${personInfo})` : personInfo;
      }

      return typeName;
    },
    
    formatAmountShort(value) {
      if (value === null || value === undefined) return '-';
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'М';
      }
      if (value >= 1000) {
        return Math.round(value / 1000) + 'К';
      }
      return Math.round(value) + '';
    },
    
    getInvoiceTooltip(row) {
      let lines = [];

      if (row.is_show_sum_price) {
        lines.push(`Общая сумма: ${this.formatAmount(row.items_total)}`);

        if (row.invoiced_sum > 0) {
          lines.push(`В накладных: ${this.formatAmount(row.invoiced_sum)}`);
        }

        if (row.not_invoiced_sum > 0) {
          lines.push(`Без накладной: ${this.formatAmount(row.not_invoiced_sum)}`);
        }
      }

      if (row.invoices_json && row.invoices_json.length) {
        if (lines.length) lines.push('');
        lines.push('Накладные:');
        row.invoices_json.forEach(inv => {
          lines.push(`  №${inv.num} от ${inv.date}`);
        });
      }

      return lines.join('\n');
    },
    
    // Методы для галочки статуса позиции
    isItemAvailable(item) {
      // Галочка отмечена если статус "Есть в наличии" (200) или "Отгружен" (300)
      return item.provider_request_status_id === 200 || item.provider_request_status_id === 300;
    },
    
    isItemCheckboxDisabled(item) {
      // Заблокирована если статус "Отгружен" (300) или другие статусы кроме 100, 200, 1500
      const statusId = item.provider_request_status_id;
      return statusId === 300 || ![100, 200, 1500].includes(statusId);
    },
    
    getItemCheckboxTitle(item) {
      const statusId = item.provider_request_status_id;
      if (statusId === 300) {
        return 'Товар отгружен - изменение невозможно';
      }
      if (statusId === 200) {
        return 'Снять отметку "Есть в наличии"';
      }
      if (statusId === 100 || statusId === 1500) {
        return 'Отметить "Есть в наличии"';
      }
      return 'Изменение статуса недоступно';
    },
    
    async changeItemStatusTo(item, newStatusId) {
      // Запоминаем provider_request_id для последующего обновления
      const providerRequestId = item.provider_request_id;
      
      try {
        const result = await changeItemStatus(item.provider_request_item_id, newStatusId);
        if (result?.provider_request_status_id) {
          // Обновляем статус в локальных данных позиции
          item.provider_request_status_id = result.provider_request_status_id;
          // Обновляем название статуса
          const statusNames = {
            100: 'Создан',
            200: 'Есть в наличии',
            300: 'Отгружен',
            700: 'Отменен',
            1000: 'Нет в наличии',
          };
          item.provider_request_status_name = statusNames[result.provider_request_status_id] || 'Неизвестно';
          
          // Реактивно обновляем данные заявки (статусы, накладные и т.д.)
          await this.refreshRequest(providerRequestId);
          
          this.$q.notify({
            type: 'positive',
            message: `Статус изменён на "${item.provider_request_status_name}"`,
            timeout: 2000
          });
        }
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка изменения статуса',
        });
      }
    },
    
    // Обновление данных одной заявки без перезагрузки всего списка
    async refreshRequest(providerRequestId) {
      try {
        // Получаем актуальные данные заявки
        const res = await getSupplierRequests({ 
          provider_request_id: providerRequestId,
          page: 1,
          limit: 1
        });
        
        if (res?.data?.length > 0) {
          const updatedRequest = res.data[0];
          
          // Находим индекс заявки в списке
          const index = this.rowsData.findIndex(
            r => r.provider_request_id === providerRequestId
          );
          
          if (index !== -1) {
            // Обновляем данные заявки в списке
            this.rowsData.splice(index, 1, updatedRequest);
          }
        }
        
        // Также обновляем позиции заявки если они загружены
        if (this.itemsData[providerRequestId]) {
          const items = await getSupplierRequestItems(providerRequestId);
          this.itemsData[providerRequestId] = items;
        }
      } catch (e) {
        console.error('Error refreshing request:', e);
      }
    },
    
    canChangeToStatus(item, targetStatus) {
      const current = item.provider_request_status_id;
      
      // Автоматические статусы (накладные) и Отменен - нельзя менять вручную
      // 200 - Есть в наличии, 250 - Частично есть в наличии
      // 300 - Отгружен, 350 - Частично отгружен, 700 - Отменен
      const autoStatuses = [200, 250, 300, 350, 700];
      if (autoStatuses.includes(current)) return false;
      
      // Можно менять только на: Нет в наличии (1000) или Отменен (700)
      if (targetStatus !== 1000 && targetStatus !== 700) return false;
      
      // Нельзя выбрать текущий статус
      if (current === targetStatus) return false;
      
      // Разрешённые переходы:
      // 100 (Создан) -> 1000, 700
      // 1000 (Нет в наличии) -> 700
      const allowedTransitions = {
        100: [1000, 700],       // Создан
        1000: [700],            // Нет в наличии
      };
      
      return allowedTransitions[current]?.includes(targetStatus) || false;
    },
    
    getInvoiceStatusColor(status) {
      const colors = {
        'Нет накладных': 'grey',
        'Частично': 'orange',
        'Полностью': 'green',
      };
      return colors[status] || 'grey';
    },
    
    // Debounce для автоматической фильтрации
    debouncedGetData() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.expandedRows = {};
        this.itemsData = {};
        this.realizesData = {};
        this.selectedRequests = {};
        this.getData();
      }, 500);
    },
    
    // Методы для работы с URL
    updateUrlParams() {
      const query = {};
      
      if (this.filters.date_shipping_begin) {
        query.date_begin = this.filters.date_shipping_begin;
      }
      if (this.filters.date_shipping_end) {
        query.date_end = this.filters.date_shipping_end;
      }
      if (this.filters.date_create_begin) {
        query.create_begin = this.filters.date_create_begin;
      }
      if (this.filters.date_create_end) {
        query.create_end = this.filters.date_create_end;
      }
      if (this.filters.resident_id) {
        query.resident_id = this.filters.resident_id;
      }
      if (this.filters.project_type_id) {
        query.project_type_id = this.filters.project_type_id;
      }
      if (this.selectedProject?.resident_name) {
        query.resident_name = this.selectedProject.resident_name;
      }
      if (this.selectedProject?.project_type_name) {
        query.project_type_name = this.selectedProject.project_type_name;
      }
      if (this.filters.provider_request_status_id_arr?.length) {
        query.statuses = this.filters.provider_request_status_id_arr.join(',');
      }
      if (this.filters.provider_request_initiator_type_id) {
        query.initiator_type = this.filters.provider_request_initiator_type_id;
      }
      if (this.filters.provider_request_id) {
        query.request_id = this.filters.provider_request_id;
      }
      if (this.filters.invoice_num) {
        query.invoice_num = this.filters.invoice_num;
      }
      if (this.filters.provider_request_invoice_id) {
        query.invoice_id = this.filters.provider_request_invoice_id;
      }
      if (this.filters.material_id) {
        query.material_id = this.filters.material_id;
      }
      if (this.selectedMaterial?.material_name) {
        query.material_name = this.selectedMaterial.material_name;
      }
      if (this.filters.page > 1) {
        query.page = this.filters.page;
      }
      if (this.filters.send_to_1c_filter) {
        query.send_1c = this.filters.send_to_1c_filter;
      }
      if (this.filters.provider_id) {
        query.provider_id = this.filters.provider_id;
      }
      if (this.filters.sbs_project_id) {
        query.sbs_project_id = this.filters.sbs_project_id;
      }
      if (this.selectedSbsProject?.naimenovanie) {
        query.sbs_project_name = this.selectedSbsProject.naimenovanie;
      }
      if (this.filters.required_type_id) {
        query.required_type_id = this.filters.required_type_id;
      }
      
      // Обновляем URL без перезагрузки
      const newUrl = Object.keys(query).length 
        ? `${window.location.pathname}?${new URLSearchParams(query).toString()}`
        : window.location.pathname;
      
      window.history.replaceState({}, '', newUrl);
    },
    
    loadFromUrl() {
      const params = new URLSearchParams(window.location.search);
      
      if (params.get('date_begin')) {
        this.filters.date_shipping_begin = params.get('date_begin');
      }
      if (params.get('date_end')) {
        this.filters.date_shipping_end = params.get('date_end');
      }
      if (params.get('create_begin')) {
        this.filters.date_create_begin = params.get('create_begin');
      }
      if (params.get('create_end')) {
        this.filters.date_create_end = params.get('create_end');
      }
      if (params.get('resident_id')) {
        this.filters.resident_id = parseInt(params.get('resident_id'));
      }
      if (params.get('project_type_id')) {
        this.filters.project_type_id = parseInt(params.get('project_type_id'));
      }
      if (params.get('statuses')) {
        this.filters.provider_request_status_id_arr = params.get('statuses').split(',').map(Number);
      }
      if (params.get('initiator_type')) {
        this.filters.provider_request_initiator_type_id = parseInt(params.get('initiator_type'));
      }
      if (params.get('request_id')) {
        this.filters.provider_request_id = parseInt(params.get('request_id'));
      }
      if (params.get('invoice_num')) {
        this.filters.invoice_num = params.get('invoice_num');
      }
      if (params.get('invoice_id')) {
        this.filters.provider_request_invoice_id = parseInt(params.get('invoice_id'));
      }
      if (params.get('material_id')) {
        this.filters.material_id = parseInt(params.get('material_id'));
      }
      if (params.get('page')) {
        this.filters.page = parseInt(params.get('page'));
      }
      const send1c = params.get('send_1c');
      if (send1c === 'SENT' || send1c === 'NOT_SENT') {
        this.filters.send_to_1c_filter = send1c;
      }
      if (params.get('provider_id')) {
        const pid = parseInt(params.get('provider_id'), 10);
        if (Number.isFinite(pid)) {
          this.filters.provider_id = pid;
        }
      }
      
      // Если есть material_id в URL, восстанавливаем selectedMaterial
      if (this.filters.material_id) {
        this.selectedMaterial = {
          material_id: this.filters.material_id,
          material_name: decodeURIComponent(params.get('material_name') || 'Материал')
        };
      }
      
      // Если есть resident_id и project_type_id в URL, восстанавливаем selectedProject
      if (this.filters.resident_id || this.filters.project_type_id) {
        this.selectedProject = {
          resident_id: this.filters.resident_id,
          project_type_id: this.filters.project_type_id,
          resident_name: decodeURIComponent(params.get('resident_name') || 'ЖК'),
          project_type_name: decodeURIComponent(params.get('project_type_name') || 'Тип')
        };
      }

      if (params.get('sbs_project_id')) {
        const sid = parseInt(params.get('sbs_project_id'), 10);
        if (Number.isFinite(sid)) {
          this.filters.sbs_project_id = sid;
          this.selectedSbsProject = {
            sbs_project_id: sid,
            naimenovanie: decodeURIComponent(params.get('sbs_project_name') || `SBS #${sid}`)
          };
        }
      }
      if (params.get('required_type_id')) {
        const rtid = parseInt(params.get('required_type_id'), 10);
        if (Number.isFinite(rtid)) {
          this.filters.required_type_id = rtid;
        }
      }
    },
    
    async loadDictionaries() {
      const [statusesRes, purchaseTypesRes, initiatorTypesRes, requiredTypesRes] = await Promise.all([
        getStatuses(),
        getPurchaseTypes(),
        getInitiatorTypes(),
        getRequiredTypes(),
      ]);
      
      this.statuses = statusesRes || [];
      this.purchaseTypes = purchaseTypesRes || [];
      this.initiatorTypes = initiatorTypesRes || [];
      this.requiredTypes = (requiredTypesRes || []).filter(rt => rt.is_active !== false).slice(2);
    },

    async loadProviderFilterOptions() {
      const res = await getProviders({});
      this.providerFilterOptions = Array.isArray(res) ? res : [];
    },

    async confirmDeletePoddonItem(providerRequestId, row) {
      if (row?.material_code !== 'PODDON') return;

      if (row?.provider_request_status_id !== 100) {
        this.$q.notify({
          type: 'negative',
          message: 'Удаление доступно только в статусе "Создан"',
        });
        return;
      }

      try {
        await this.$q.dialog({
          title: 'Подтверждение',
          message: `Удалить позицию "${row?.material_name || ''}" из заявки?`,
          cancel: true,
          persistent: true,
        });
      } catch (_) {
        return;
      }

      try {
        await deleteSupplierRequestItem(row.provider_request_item_id);
        this.$q.notify({
          type: 'positive',
          message: 'Позиция удалена',
          timeout: 1500,
        });
        await this.refreshRequest(providerRequestId);
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка удаления позиции',
        });
      }
    },
    
    async getData() {
      this.isFetching = true;

      const params = this.buildRequestParams();
      const res = await getSupplierRequests(params);
      this.isFetching = false;
      
      if (!res) return;
      
      this.rowsData = res.data || [];
      this.totalCount = res.total_count || 0;
      this.isShowAll = res.is_show_all_provider_requests || false;
      this.isShowAllActions = res.is_show_all_provider_requests_actions || false;

      this.listSortMode = this.isShowAll ? 'created_flat' : 'shipping_grouped';


      if (!this.isShowAll && this.filters.provider_id) {
        this.filters.provider_id = null;
        await this.$router.replace({
          query: { ...this.$route.query, provider_id: undefined },
        });
      } else if (this.isShowAll) {
        await this.loadProviderFilterOptions();
      } else {
        this.providerFilterOptions = [];
      }
    },

    async exportMaterials() {
      if (!this.rowsData?.length) {
        this.$q.notify({
          type: 'warning',
          message: 'Нет данных для выгрузки',
          timeout: 2000
        });
        return;
      }

      this.isExporting = true;
      await exportSupplierRequestMaterials(
        this.buildRequestParams({ includePagination: false })
      );
      this.isExporting = false;
    },
    
    openProjectModal() {
      this.appStore.showModal(MODAL_NAMES_AIDAR.SelectProjectGrouped, {
        title: 'Выбор ЖК и типа проекта',
        resident_id: this.filters.resident_id,
        project_type_id: this.filters.project_type_id,
        onSelect: (selected) => {
          if (selected) {
            this.filters.resident_id = selected.resident_id;
            this.filters.project_type_id = selected.project_type_id;
            this.selectedProject = selected;
          }
        }
      });
    },

    async openSbsProjectModal() {
      this.sbsProjectDialog.show = true;
      this.sbsProjectSearch = '';
      this.sbsProjectPage = 1;
      this.sbsProjectTotalCount = 0;
      this.sbsProjectList = [];
      await this.loadSbsProjects();
    },

    async loadSbsProjects(page) {
      if (page) this.sbsProjectPage = page;
      this.isLoadingSbsProjects = true;
      const data = await getSbsProjects(this.sbsProjectSearch || '', this.sbsProjectPage, this.sbsProjectLimit);
      this.isLoadingSbsProjects = false;
      if (Array.isArray(data) && data.length > 0) {
        this.sbsProjectList = data;
        this.sbsProjectTotalCount = data[0].total_count || data.length;
      } else {
        this.sbsProjectList = [];
        this.sbsProjectTotalCount = 0;
      }
    },

    onSbsSearchInput() {
      if (this.sbsSearchDebounce) clearTimeout(this.sbsSearchDebounce);
      this.sbsSearchDebounce = setTimeout(() => {
        this.sbsProjectPage = 1;
        this.loadSbsProjects();
      }, 400);
    },

    selectSbsProject(row) {
      this.filters.sbs_project_id = row.sbs_project_id;
      this.selectedSbsProject = row;
      this.sbsProjectDialog.show = false;
    },

    clearSbsProject() {
      this.filters.sbs_project_id = null;
      this.selectedSbsProject = null;
    },
    
    // === Мульти-выбор заявок ===
    isRequestSelected(row) {
      return !!this.selectedRequests[row.provider_request_id];
    },
    toggleRequestSelection(row) {
      if (this.selectedRequests[row.provider_request_id]) {
        delete this.selectedRequests[row.provider_request_id];
        this.selectedRequests = { ...this.selectedRequests };
      } else {
        this.selectedRequests = {
          ...this.selectedRequests,
          [row.provider_request_id]: row
        };
      }
    },
    clearSelection() {
      this.selectedRequests = {};
    },
    openCreateInvoiceForSelected() {
      const requests = this.selectedRequestsList;
      if (!requests.length) return;
      
      this.appStore.showModal(MODAL_NAMES_AIDAR.CreateInvoice, {
        title: requests.length === 1
          ? `Создание накладной для заявки #${requests[0].provider_request_id}`
          : `Создание накладной для ${requests.length} заявок`,
        requests: requests,
        onSuccess: async () => {
          for (const req of requests) {
            await this.refreshRequest(req.provider_request_id);
          }
          this.clearSelection();
        }
      });
    },

    openUploadInvoiceForSelected() {
      const requests = this.selectedRequestsList;
      if (!requests.length) return;

      this.appStore.showModal(MODAL_NAMES_AIDAR.CreateInvoice, {
        title: requests.length === 1
          ? `Загрузка накладной вручную для заявки #${requests[0].provider_request_id}`
          : `Загрузка общей накладной вручную для ${requests.length} заявок`,
        requests: requests,
        is_manual_mode: true,
        onSuccess: async () => {
          for (const req of requests) {
            await this.refreshRequest(req.provider_request_id);
          }
          this.clearSelection();
        }
      });
    },
    
    openCreateInvoice(request) {
      if (!request.can_create_invoice) return;
      
      const providerRequestId = request.provider_request_id;
      
      this.appStore.showModal(MODAL_NAMES_AIDAR.CreateInvoice, {
        title: `Создание накладной для заявки #${providerRequestId}`,
        requests: [request],
        onSuccess: async () => {
          await this.refreshRequest(providerRequestId);
        }
      });
    },
    
    openUploadInvoice(request) {
      if (!request.is_show_add_manual_invoice) return;
      
      const providerRequestId = request.provider_request_id;
      
      this.appStore.showModal(MODAL_NAMES.UploadInvoice, {
        title: `Загрузка подписанной накладной для заявки #${providerRequestId}`,
        request: request,
        onSuccess: async () => {
          // Реактивно обновляем только эту заявку
          await this.refreshRequest(providerRequestId);
        }
      });
    },

    openAddMaterial(request) {
      const providerRequestId = request.provider_request_id;
      this.appStore.showModal(MODAL_NAMES_AIDAR.SupplierRequestAddMaterial, {
        title: `Добавить материал в заявку #${providerRequestId}`,
        provider_request_id: providerRequestId,
        onSuccess: async () => {
          await this.refreshRequest(providerRequestId);
        }
      });
    },
    
    getCreateInvoiceTitle(request) {
      if (!request.can_create_invoice) {
        if (request.available_for_invoice_cnt === 0) {
          return 'Нет позиций со статусом "Создан", "Частично есть в наличии"';
        }
        if (request.remaining_for_invoice_cnt <= 0) {
          return 'На все позиции уже созданы накладные';
        }
        return 'Невозможно создать накладную';
      }
      return 'Создать накладную';
    },
    
    openViewInvoice(invoice, providerRequestId) {
      this.appStore.showModal(MODAL_NAMES_AIDAR.ViewInvoice, {
        title: `Накладная №${invoice.num} от ${invoice.date}`,
        invoice: invoice,
        isShowAll: this.isShowAll,
        showActions: this.showActions,
        onDeleted: async () => {
          // Перезагружаем данные заявки после удаления накладной
          await this.refreshRequest(providerRequestId);
        },
        onUpdated: async () => {
          // Перезагружаем данные заявки после редактирования накладной
          await this.refreshRequest(providerRequestId);
        }
      });
    },
    
    async loadRealizes(providerRequestId) {
      this.loadingRealizes[providerRequestId] = true;
      try {
        const realizes = await getRealizes(providerRequestId);
        this.realizesData[providerRequestId] = realizes || [];
      } catch (e) {
        console.error('Error loading realizes:', e);
        this.realizesData[providerRequestId] = [];
      } finally {
        this.loadingRealizes[providerRequestId] = false;
      }
    },
    
    openViewRealize(realize) {
      this.appStore.showModal(MODAL_NAMES_AIDAR.ViewRealize, {
        title: `Реализация №${realize.realize_num} от ${realize.realize_date}`,
        realize: realize
      });
    },
    
    clearProject() {
      this.filters.resident_id = null;
      this.filters.project_type_id = null;
      this.selectedProject = null;
    },
    
    openMaterialModal() {
      this.appStore.showModal(MODAL_NAMES_AIDAR.SelectMaterial, {
        title: 'Выберите материал',
        material_id: this.filters.material_id,
        onSelect: (material) => {
          this.filters.material_id = material.material_id;
          this.selectedMaterial = material;
        }
      });
    },
    
    clearMaterial() {
      this.filters.material_id = null;
      this.selectedMaterial = null;
    },
    
    getStatusName(statusId) {
      const status = this.availableStatuses.find(s => s.provider_request_status_id === statusId);
      return status?.provider_request_status_name || '';
    },
    
    parseStatusSummary(statusSummary) {
      if (!statusSummary || statusSummary === 'Нет позиций') {
        return [{ name: 'Нет позиций', count: 0, color: 'grey' }];
      }
      
      // Парсим строку вида "Есть в наличии (1)"
      const statusColorMap = {
        'Создан': 'grey',
        'Есть в наличии': 'green',
        'Частично есть накладная': 'blue',
        'Частично отгружен': 'light-blue',
        'Отгружен': 'light-green',
        'Отменен': 'red',
        'Нет в наличии': 'orange',
      };
      
      const parts = statusSummary.split(', ');
      return parts.map(part => {
        const match = part.match(/^(.+?)\s*\((\d+)\)$/);
        if (match) {
          const name = match[1].trim();
          const count = parseInt(match[2], 10);
          const color = statusColorMap[name] || 'grey';
          return { name, count, color };
        }
        return { name: part, count: 0, color: 'grey' };
      });
    },
    
    applyFilters() {
      this.expandedRows = {};
      this.itemsData = {};
      this.realizesData = {};
      this.selectedRequests = {};
      this.getData();
    },
    
    resetFilters() {
      this.filters = {
        date_shipping_begin: null,
        date_shipping_end: null,
        date_create_begin: null,
        date_create_end: null,
        resident_id: null,
        project_type_id: null,
        sbs_project_id: null,
        required_type_id: null,
        provider_request_status_id_arr: [],
        provider_request_initiator_type_id: null,
        provider_request_id: null,
        invoice_num: null,
        provider_request_invoice_id: null,
        material_id: null,
        invoice_filter_type: null,
        send_to_1c_filter: null,
        provider_id: null,
        page: 1,
        limit: 50
      };
      this.selectedProject = null;
      this.selectedSbsProject = null;
      this.selectedMaterial = null;
      this.applyFilters();
    },
    
    async toggleExpand(row) {
      const id = row.provider_request_id;
      
      if (this.expandedRows[id]) {
        this.expandedRows[id] = false;
        return;
      }
      
      this.expandedRows[id] = true;
      
      // Загружаем позиции, если ещё не загружены
      if (!this.itemsData[id]) {
        this.loadingItems[id] = true;
        const items = await getSupplierRequestItems(id);
        // attach parent request id to each item for later actions
        if (Array.isArray(items)) {
          items.forEach((it) => {
            it.provider_request_id = id;
          });
        }
        this.itemsData[id] = items;
        this.loadingItems[id] = false;
      }
      
      // Загружаем реализации, если ещё не загружены
      if (this.realizesData[id] === undefined) {
        this.loadRealizes(id);
      }
    },
    openUploadModal(item) {
      // Resolve projectId carefully: prefer `project_id` (project_tab id) over `resident_id`.
      let projectId = item.project_id || null;
      if (!projectId && item?.provider_request_id) {
        const parent = this.rowsData.find(r => r.provider_request_id === item.provider_request_id);
        if (parent) projectId = parent.project_id || null;
      }
      // Do NOT use resident_id fallback — require actual project_id.
      if (!projectId && this.selectedProject) {
        projectId = this.selectedProject?.project_id || null;
      }

      if (!projectId) {
        console.warn('AddMaterialCertificate: resolved projectId is empty for item', item);
        this.$q.notify({ type: 'negative', message: 'Не удалось определить проект (project_id) для загрузки сертификата' });
        return;
      }

      this.appStore.showModal(MODAL_NAMES.AddMaterialCertificate, {
        title: "Добавить сертификат",
        projectId,
        material: item,
        onSubmit: async (res) => {
          if (item?.provider_request_id) {
            await this.refreshRequest(item.provider_request_id);
          }
        },
      });
    },
    async onDeleteCertificate(row) {
      const projectMaterialCertificateId = row.project_material_certificate_id;

      // Resolve project id: prefer project_id, then parent's project_id, then selectedProject.project_id
      let projectId = row.project_id || null;
      if (!projectId && row?.provider_request_id) {
        const parent = this.rowsData.find((r) => r.provider_request_id === row.provider_request_id);
        if (parent) projectId = parent.project_id || null;
      }
      if (!projectId && this.selectedProject) projectId = this.selectedProject?.project_id || null;

      if (!projectId || !projectMaterialCertificateId || this.deleting[projectMaterialCertificateId]) return;

      this.deleting = { ...this.deleting, [projectMaterialCertificateId]: true };
      try {
        const res = await deleteProjectMaterialCertificate(projectId, projectMaterialCertificateId);
        this.deleting = { ...this.deleting, [projectMaterialCertificateId]: false };
        if (!res) return;
        // refresh request and items
        if (row?.provider_request_id) await this.refreshRequest(row.provider_request_id);
        this.showSucceedSnackbar();
      } catch (e) {
        this.deleting = { ...this.deleting, [projectMaterialCertificateId]: false };
        this.showErrorSnackbar(e?.response?.data?.error || 'Ошибка удаления сертификата');
      }
    },

    async confirmDeleteCertificate(row) {
      try {
        await this.$q.dialog({
          title: 'Подтверждение',
          message: `Удалить сертификат для позиции "${row.material_name || ''}"?`,
          cancel: true,
          persistent: true,
        });
      } catch (_) {
        return; // user cancelled
      }

      // call delete logic
      await this.onDeleteCertificate(row);
    },
  },
  async mounted() {
    this.loadSettings();
    this.loadFromUrl();
    await this.loadDictionaries();
    await this.getData();
    this.isInitialLoad = false;
  },
  setup() {
    const appStore = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { appStore, showSucceedSnackbar, showErrorSnackbar };
  }
};
</script>

<style scoped>
.supplier-requests {
  min-height: 100vh;
}

.certificates__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Helpers to match Projects view */
.certificates__cert-number {
  display: inline-flex;
  align-items: center;
  background: #eef8f3;
  color: #0b6b3a;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}
.certificates__download-link {
  color: #1f5ac2;
  font-weight: 600;
}
.certificates__add-link {
  background: #eefaf6;
  color: #1f8a5a;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 13px;
}

.certificates__center-wrap {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

/* Вкладки быстрых фильтров */
.quick-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;
}

.quick-filter-toggle {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.quick-filter-toggle :deep(.q-btn) {
  padding: 4px 12px;
  font-weight: 500;
  font-size: 12px;
  min-height: 28px;
  
  .q-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.quick-filter-toggle :deep(.q-btn--active) {
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15);
}

.filters-section {
  background: #f7f7f7;
  padding: 12px 16px;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
}

.filters-section.filters-collapsed {
  padding: 8px 16px;
}

.filters-header {
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 8px;
  user-select: none;
  transition: all 0.2s;
}

.filters-collapsed .filters-header {
  margin-bottom: 0;
}

.filters-header:hover {
  color: #1976d2;
}

.filters-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #424242;
}

.filters-header:hover .filters-title {
  color: #1976d2;
}

.filters-summary {
  font-size: 12px;
  color: #757575;
  flex: 1;
}

.filters-content {
  animation: fadeIn 0.2s ease;
}

.filters-row {
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.filters-row:last-child {
  margin-bottom: 0;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 120px;
  flex-shrink: 0;
}

/* Компактные поля ввода */
.filter-item :deep(.q-field) {
  .q-field__control {
    min-height: 32px;
    height: 32px;
    align-items: center;
  }
  
  .q-field__native,
  .q-field__prefix,
  .q-field__suffix {
    font-size: 13px;
    padding: 0 8px;
    line-height: 32px;
  }
  
  .q-field__marginal {
    height: 32px;
  }
}

/* Для q-select: центрируем display-value (иначе едет вниз) */
.filter-item :deep(.q-field__control-container) {
  align-items: center;
}

.filter-item :deep(.q-field__native) {
  display: flex;
  align-items: center;
}

.filter-item--date {
  width: 120px;
  min-width: 120px;
  max-width: 120px;
  flex-shrink: 0;
}

.filter-item--status {
  min-width: 140px;
  flex: 1;
  max-width: 200px;
}

.filter-item--grow {
  flex: 1;
  min-width: 260px;
}

.project-input {
  width: 160px;
  cursor: pointer;
}

.project-input :deep(.q-field__control) {
  cursor: pointer;
}

.filter-with-clear {
  display: flex;
  align-items: center;
  gap: 2px;
}

.filter-input-wrapper {
  cursor: pointer;
}

.filter-input-wrapper--grow {
  width: 100%;
}

.material-input {
  width: 150px;
  pointer-events: none;
}

.project-input--grow,
.material-input--grow {
  width: 100%;
  max-width: 100%;
}

.material-input :deep(.q-field__control),
.project-input :deep(.q-field__control) {
  cursor: pointer;
}

.clear-btn {
  margin-left: -6px;
  
  :deep(.q-btn__content) {
    padding: 2px;
  }
}

.filter-item--small {
  max-width: 240px;
  min-width: 200px;
  
  .q-field {
    width: 200px;
  }
}

.filter-item--medium {
  max-width: 280px;
  min-width: 240px;
  
  .q-field {
    width: 240px;
  }
}

.filter-item--invoice-filter {
  min-width: 220px;
  max-width: 280px;
}

.status-select {
  min-width: 120px;
}

.status-select :deep(.q-field__control) {
  min-height: 32px;
}

.status-select :deep(.q-chip) {
  margin: 1px;
  height: 20px;
  font-size: 11px;
}

.status-single {
  font-size: 12px;
  color: #333;
  line-height: 15px;
}

.status-count {
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
}

.status-placeholder {
  font-size: 12px;
  color: #9e9e9e;
}

/* Компактный dropdown для статусов */
.status-select :deep(.q-menu) {
  .q-item {
    min-height: 24px;
    padding: 2px 8px;
  }
  
  .q-item__section--side {
    padding-right: 4px;
  }
  
  .q-checkbox {
    padding: 0;
    
    .q-checkbox__inner {
      font-size: 14px;
    }
  }
  
  .q-badge {
    font-size: 11px;
    padding: 2px 6px;
  }
}

.filter-item label {
  font-size: 10px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0;
}

.filter-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
  align-items: flex-end;
}

.filter-actions .q-btn {
  font-size: 12px;
  padding: 4px 12px;
  min-height: 32px;
}

.table-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  :deep(.table-sort-select) {
    min-width: 260px;
  }
}

.table-sort-toolbar {
  padding-right: 4px;
}

.id-initiator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.id-initiator strong {
  font-size: 13px;
}

.date-created-cell {
  font-size: 12px;
  color: #424242;
  white-space: nowrap;
}

.project-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.project-info strong {
  font-size: 13px;
  color: #1976d2;
}

.resident-name {
  font-size: 11px;
  color: #6c757d;
}

.provider-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.provider-info .q-badge:not(.purchase-type-badge) {
  width: fit-content;
}

/* Тип закупки: ширина + текст по центру (в колонке и внутри бейджа) */
.provider-info .purchase-type-badge.q-badge {
  max-width: 200px;
  min-width: 150px;
  line-height: 1.25;
  align-self: center;
  text-align: center;
  justify-content: center;
  align-items: center;
}

.provider-info .purchase-type-badge.q-badge.q-badge--multi-line {
  word-break: normal;
  overflow-wrap: break-word;
}

.provider-name {
  font-size: 13px;
  font-weight: 500;
}

.not-invoiced {
  font-size: 12px;
  color: #9e9e9e;
}

.not-invoiced.has-remaining {
  color: #e65100;
  font-weight: 600;
}

.date-cell {
  font-weight: 500;
}

.status-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
}

.status-badges-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}

.status-badge-item {
  font-size: 12px;
  padding: 3px 6px;
  line-height: 1.25;
  min-width: 100px;
}

/* q-badge--multi-line по умолчанию задаёт word-break: break-all — перенос по словам */
.status-badge-item.q-badge.q-badge--multi-line {
  word-break: normal;
  overflow-wrap: break-word;
  justify-content: center;
}

.status-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.status-checkbox {
  margin-right: 2px;
}

.status-dropdown {
  font-size: 11px;
  
  :deep(.q-btn__content) {
    padding: 2px 6px;
  }
}

.project-type {
  font-size: 11px;
  color: #6c757d;
}

.items-shipping-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.items-count {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 12px;
}

.nearest-shipping {
  font-size: 11px;
  color: #5c6bc0;
  font-weight: 500;
}

.amount {
  font-weight: 600;
  color: #2e7d32;
}

.invoice-combined {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.invoices-list-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  text-align: left;
}

.invoice-item-block {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  background: #e8f5e9;
  transition: background 0.15s;
  text-align: left;
  
  &:hover {
    background: #c8e6c9;
  }
  
  &--unsigned {
    background: #fff3e0;
    border-left: 3px solid #ef6c00;

    &:hover {
      background: #ffe0b2;
    }

    .invoice-num-line {
      color: #e65100;
    }
  }

  &--manual {
    background: #fff8e1;
    border-left: 3px solid #ff8f00;

    &:hover {
      background: #ffecb3;
    }

    .invoice-num-line {
      color: #6d4c00;
    }
  }

  &--pending {
    background: #fff3cd;
    border-left: 3px solid #f9a825;

    &:hover {
      background: #fce4a1;
    }

    .invoice-num-line {
      color: #b8860b;
    }
  }

  &--rejected {
    background: #ffebee;
    border-left: 3px solid #c62828;

    &:hover {
      background: #ffcdd2;
    }

    .invoice-num-line {
      color: #b71c1c;
    }
  }
}

.invoice-num-line {
  color: #2e7d32;
  font-weight: 600;
  font-size: 12px;
  display: flex;
  align-items: center;
}

.invoice-date-sum-line {
  display: flex;
  gap: 4px;
  align-items: center;
}

.invoice-date {
  color: #666;
  font-size: 11px;
}

.invoice-sum {
  color: #1565c0;
  font-weight: 500;
  font-size: 11px;
}

.not-invoiced-row {
  font-size: 11px;
  color: #e65100;
  font-weight: 500;
  background: #fff3e0;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Реализации */
.realizes-combined {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.realizes-list-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  text-align: left;
}

.realize-item-block {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  background: #e3f2fd;
  transition: background 0.15s;
  text-align: left;
  
  &:hover {
    background: #bbdefb;
  }
}

.realize-num-line {
  color: #1565c0;
  font-weight: 600;
  font-size: 12px;
}

.realize-date-sum-line {
  display: flex;
  gap: 4px;
  align-items: center;
}

.realize-date {
  color: #666;
  font-size: 11px;
}

.realize-sum {
  color: #2e7d32;
  font-weight: 500;
  font-size: 11px;
}

.invoices-cell {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.invoices-text {
  font-size: 12px;
  color: #2e7d32;
  max-width: 200px;
  white-space: normal;
  line-height: 1.3;
}

.no-invoice {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #e65100;
}

.remaining-text {
  color: #9e9e9e;
  font-size: 11px;
}

.expand-icon {
  color: #6c757d;
  transition: transform 0.2s ease;
}

.expanded-row {
  background: #f0f7ff !important;
}

.expanded-row .expand-icon {
  color: #1976d2;
}

.details-row {
  background: #f0f4ff;
}

.details-row > td {
  padding: 0 !important;
}

.details-container {
  padding: 20px;
  margin: 8px 12px 16px 12px;
  border: 2px solid #1976d2;
  border-radius: 8px;
  background: white;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
  }
}

.details-header {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border-radius: 6px;
  color: white;
}

.details-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.details-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.details-header h4 {
  margin: 0;
  font-size: 16px;
  color: white;
  font-weight: 600;
}

.details-info {
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.details-info strong {
  color: white;
}

.loading-items {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  color: #6c757d;
}

.items-table {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.items-table :deep(thead tr th) {
  background: #f8f9fa;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #495057;
}

.material-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.material-info {
  max-width: 250px;
  white-space: normal;
  line-height: 1.3;
}

.material-name {
  font-weight: 500;
  display: block;
}

.material-code {
  font-size: 11px;
  color: #6c757d;
}

.provider-meta {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.provider-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.provider-block--identical {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-left: 4px solid #2e7d32;
  border-radius: 6px;
  padding: 6px 8px;
}

.provider-head {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.provider-label {
  color: #9e9e9e;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.provider-value {
  color: #455a64;
  font-size: 12px;
  line-height: 1.25;
}

.provider-value--provider {
  color: #1565c0;
  font-weight: 600;
}

.provider-badge {
  font-size: 10px;
  padding: 2px 6px;
}

.unit-name {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.no-data {
  color: #bdbdbd;
}

.no-items {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

.pagination-section {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 16px;
  border-top: 1px solid #e9ecef;
}

.total-info {
  font-size: 13px;
  color: #6c757d;
}

/* Стили для заголовков групп (дата) */
.group-header-row {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
  border-top: 2px solid #1976d2;
}

.group-header-cell {
  padding: 12px 16px !important;
}

.group-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.group-date-label {
  font-size: 15px;
  font-weight: 700;
  color: #1565c0;
  letter-spacing: 0.3px;
}

.group-stats {
  display: flex;
  gap: 12px;
  align-items: center;
}

.group-stats .q-badge {
  font-size: 12px;
  padding: 4px 10px;
  font-weight: 500;
}

/* Мульти-выбор заявок */
.selected-request-row {
  background: #e8eaf6 !important;
}

.selected-request-row:hover {
  background: #c5cae9 !important;
}

.selection-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #1565c0 0%, #1976d2 100%);
  color: white;
  padding: 14px 24px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
  z-index: 9999;
}

.selection-bar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.selection-info {
  font-size: 15px;
  display: flex;
  align-items: center;
}

.selection-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>

